/**
 * Peak State Engine - Performance Activation Tool
 * 
 * 60-90 second guided experience that shifts users into peak performance state.
 * NOT motivation. IMMEDIATE state activation before action.
 * 
 * Flow: Entry → Reset → Identity → Visualisation → Activation → Ready
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  RotateCcw,
  Target,
  Briefcase,
  Mic,
  Dumbbell,
  ChevronRight
} from 'lucide-react';

// Context configurations with specific visualizations and text
const CONTEXTS = {
  golf: {
    id: 'golf',
    title: 'Golf',
    icon: Target,
    color: '#4A6741',
    visualisation: {
      text: 'See the perfect swing... smooth backswing... clean contact... the ball flying exactly where you intended...',
      keywords: ['Smooth', 'Precise', 'Effortless']
    },
    // Pre-recorded audio files by level
    audio: {
      beginner: '/api/static/audio/golf/golf_beginner.mp3',
      intermediate: '/api/static/audio/golf/golf_intermediate.mp3',
      advanced: '/api/static/audio/golf/golf_advanced.mp3'
    }
  },
  sport: {
    id: 'sport',
    title: 'Sport',
    icon: Dumbbell,
    color: '#F59E0B',
    visualisation: {
      text: 'Feel your body moving perfectly... every movement automatic... power flowing through you...',
      keywords: ['Strong', 'Fast', 'Unstoppable']
    }
  },
  business: {
    id: 'business',
    title: 'Business',
    icon: Briefcase,
    color: '#6366F1',
    visualisation: {
      text: 'See yourself in the room... calm, confident presence... every word landing perfectly...',
      keywords: ['Clear', 'Commanding', 'Decisive']
    }
  },
  speaking: {
    id: 'speaking',
    title: 'Speaking',
    icon: Mic,
    color: '#EC4899',
    visualisation: {
      text: 'Feel the words flowing effortlessly... the audience connected... your message landing...',
      keywords: ['Eloquent', 'Captivating', 'Powerful']
    }
  }
};

// Stage configurations with timing
const STAGES = [
  {
    id: 'reset',
    duration: 12000, // 12 seconds
    title: 'RESET',
    voice: [
      { text: 'Take a slow breath in...', delay: 0 },
      { text: 'and release all tension...', delay: 3000 },
      { text: 'Let everything unnecessary drop...', delay: 7000 }
    ]
  },
  {
    id: 'identity',
    duration: 17000, // 17 seconds
    title: 'IDENTITY',
    voice: [
      { text: 'You are composed...', delay: 0 },
      { text: 'focused...', delay: 3000 },
      { text: 'in control...', delay: 5000 },
      { text: "You've done this before...", delay: 10000 }
    ],
    keywords: ['Calm', 'Ready', 'Certain']
  },
  {
    id: 'visualisation',
    duration: 22000, // 22 seconds
    title: 'VISUALISE',
    voice: [
      { text: 'See yourself executing...', delay: 0 },
      { text: 'smooth... precise... effortless...', delay: 5000 },
      { text: 'Everything flows exactly as it should...', delay: 12000 }
    ]
  },
  {
    id: 'activation',
    duration: 17000, // 17 seconds
    title: 'ACTIVATE',
    voice: [
      { text: 'There is nothing to think about...', delay: 0 },
      { text: 'You are already in it...', delay: 5000 },
      { text: 'Trust it...', delay: 10000 }
    ],
    finalWord: 'EXECUTE'
  },
  {
    id: 'ready',
    duration: 5000, // 5 seconds
    title: 'READY',
    voice: [
      { text: 'You are ready.', delay: 0 }
    ]
  }
];

// Entry Screen Component
const EntryScreen = ({ onSelect }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col items-center justify-center px-6"
    >
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-[#F59E0B] text-xs uppercase tracking-[0.3em] mb-4"
      >
        Performance
      </motion.p>
      
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="font-['Playfair_Display'] text-4xl text-[#F2EFE9] mb-3 text-center"
      >
        Enter Peak State
      </motion.h1>
      
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-[#9E9A90] text-base mb-12"
      >
        Activate before you perform
      </motion.p>

      {/* Context Selection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="grid grid-cols-2 gap-4 w-full max-w-sm"
      >
        {Object.values(CONTEXTS).map((context, index) => {
          const Icon = context.icon;
          return (
            <motion.button
              key={context.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              onClick={() => onSelect(context.id)}
              className="p-6 rounded-2xl bg-[#0D0D0B] border border-[#1A1A18] hover:border-[#2C2C28] transition-all duration-300 group"
              data-testid={`context-${context.id}`}
            >
              <div 
                className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center mx-auto transition-all duration-300 group-hover:scale-110"
                style={{ backgroundColor: `${context.color}15` }}
              >
                <Icon size={24} style={{ color: context.color }} strokeWidth={1.5} />
              </div>
              <span className="text-[#F2EFE9] text-sm font-light">{context.title}</span>
            </motion.button>
          );
        })}
      </motion.div>
    </motion.div>
  );
};

// Stage Display Component
const StageDisplay = ({ stage, context, stageIndex, onComplete }) => {
  const [currentVoiceIndex, setCurrentVoiceIndex] = useState(0);
  const [showKeywords, setShowKeywords] = useState(false);
  const [showFinalWord, setShowFinalWord] = useState(false);
  const [brightness, setBrightness] = useState(0);
  const contextConfig = CONTEXTS[context];

  // Handle voice text progression
  useEffect(() => {
    const voiceTimers = stage.voice.map((v, i) => {
      return setTimeout(() => {
        setCurrentVoiceIndex(i);
      }, v.delay);
    });

    // Show keywords in identity stage
    if (stage.keywords) {
      const keywordTimer = setTimeout(() => setShowKeywords(true), 8000);
      voiceTimers.push(keywordTimer);
    }

    // Show final word in activation stage
    if (stage.finalWord) {
      const finalTimer = setTimeout(() => setShowFinalWord(true), 14000);
      voiceTimers.push(finalTimer);
    }

    // Stage completion
    const completionTimer = setTimeout(onComplete, stage.duration);

    return () => {
      voiceTimers.forEach(clearTimeout);
      clearTimeout(completionTimer);
    };
  }, [stage, onComplete]);

  // Brightness increase in activation stage
  useEffect(() => {
    if (stage.id === 'activation') {
      const interval = setInterval(() => {
        setBrightness(prev => Math.min(prev + 0.02, 0.15));
      }, 200);
      return () => clearInterval(interval);
    }
  }, [stage.id]);

  // Get visualisation text for the context
  const getVisualisationText = () => {
    if (stage.id === 'visualisation' && contextConfig) {
      return contextConfig.visualisation.text;
    }
    return stage.voice[currentVoiceIndex]?.text || '';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col items-center justify-center px-8 relative"
    >
      {/* Brightness overlay for activation */}
      {stage.id === 'activation' && (
        <motion.div
          className="absolute inset-0 bg-[#F59E0B] pointer-events-none"
          animate={{ opacity: brightness }}
        />
      )}

      {/* Breathing pulse circle */}
      <motion.div
        className="absolute w-64 h-64 rounded-full"
        style={{ backgroundColor: `${contextConfig?.color || '#F59E0B'}08` }}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Stage indicator */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        className="absolute top-1/4 text-[#F59E0B] text-[10px] uppercase tracking-[0.4em]"
      >
        {stage.title}
      </motion.p>

      {/* Main voice text */}
      <div className="relative z-10 text-center max-w-md">
        <AnimatePresence mode="wait">
          <motion.p
            key={`${stage.id}-${currentVoiceIndex}`}
            initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -20, filter: 'blur(8px)' }}
            transition={{ duration: 0.6 }}
            className="font-['Playfair_Display'] text-2xl text-[#F2EFE9] leading-relaxed"
          >
            {stage.id === 'visualisation' ? getVisualisationText() : stage.voice[currentVoiceIndex]?.text}
          </motion.p>
        </AnimatePresence>

        {/* Keywords for identity stage */}
        {stage.keywords && showKeywords && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center gap-6 mt-12"
          >
            {stage.keywords.map((keyword, i) => (
              <motion.span
                key={keyword}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.2 }}
                className="text-[#F59E0B] text-sm uppercase tracking-[0.2em]"
              >
                {keyword}
              </motion.span>
            ))}
          </motion.div>
        )}

        {/* Context keywords for visualisation */}
        {stage.id === 'visualisation' && contextConfig && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 15 }}
            className="flex justify-center gap-6 mt-12"
          >
            {contextConfig.visualisation.keywords.map((keyword, i) => (
              <motion.span
                key={keyword}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 15 + i * 0.3 }}
                className="text-sm uppercase tracking-[0.2em]"
                style={{ color: contextConfig.color }}
              >
                {keyword}
              </motion.span>
            ))}
          </motion.div>
        )}

        {/* Final word for activation stage */}
        {stage.finalWord && showFinalWord && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="mt-16"
          >
            <span className="text-[#F59E0B] text-3xl font-bold tracking-[0.3em]">
              {stage.finalWord}
            </span>
          </motion.div>
        )}
      </div>

      {/* Progress dots */}
      <div className="absolute bottom-1/4 flex gap-2">
        {STAGES.slice(0, -1).map((s, i) => (
          <motion.div
            key={s.id}
            className="w-1.5 h-1.5 rounded-full"
            style={{ 
              backgroundColor: i <= stageIndex ? '#F59E0B' : '#1A1A18'
            }}
            animate={{ 
              scale: i === stageIndex ? 1.3 : 1 
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Final "Ready" Screen
const ReadyScreen = ({ onReplay, onDeeper, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen flex flex-col items-center justify-center px-8"
    >
      <motion.h1
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 150 }}
        className="font-['Playfair_Display'] text-5xl text-[#F2EFE9] mb-16"
      >
        You are ready.
      </motion.h1>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        className="space-y-4 w-full max-w-xs"
      >
        {/* Replay */}
        <button
          onClick={onReplay}
          className="w-full py-4 rounded-2xl bg-[#0D0D0B] border border-[#1A1A18] hover:border-[#F59E0B]/30 text-[#F2EFE9] text-sm flex items-center justify-center gap-2 transition-all duration-300"
          data-testid="replay-btn"
        >
          <RotateCcw size={16} className="text-[#9E9A90]" />
          Replay
        </button>

        {/* Go Deeper */}
        <button
          onClick={onDeeper}
          className="w-full py-4 rounded-2xl border border-[#F59E0B]/30 hover:border-[#F59E0B]/60 text-[#F59E0B] text-sm flex items-center justify-center gap-2 transition-all duration-300"
          data-testid="go-deeper-btn"
        >
          Go Deeper
          <ChevronRight size={16} />
        </button>

        {/* Close */}
        <button
          onClick={onClose}
          className="w-full text-[#9E9A90] text-sm hover:text-[#F2EFE9] transition-colors"
          data-testid="close-ready-btn"
        >
          Close
        </button>
      </motion.div>
    </motion.div>
  );
};

// Main Component
export const PeakStateEngine = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState('entry'); // entry, active, ready
  const [context, setContext] = useState(searchParams.get('context') || null);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const startTimeRef = useRef(null);

  // Handle context selection
  const handleContextSelect = (contextId) => {
    setContext(contextId);
    setPhase('active');
    setCurrentStageIndex(0);
    startTimeRef.current = Date.now();
  };

  // Handle stage completion
  const handleStageComplete = useCallback(() => {
    if (currentStageIndex < STAGES.length - 1) {
      setCurrentStageIndex(prev => prev + 1);
    } else {
      setPhase('ready');
    }
  }, [currentStageIndex]);

  // Handle replay
  const handleReplay = () => {
    setCurrentStageIndex(0);
    setPhase('active');
    startTimeRef.current = Date.now();
  };

  // Handle go deeper (Private Suite)
  const handleGoDeeper = () => {
    navigate('/private-suite');
  };

  // Handle close
  const handleClose = () => {
    navigate('/abundance');
  };

  // Handle skip
  const handleSkip = () => {
    setPhase('ready');
  };

  return (
    <div className="min-h-screen bg-[#080806]">
      {/* Subtle noise texture */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Header - Skip button (always visible during active phase) */}
      {phase === 'active' && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          whileHover={{ opacity: 1 }}
          onClick={handleSkip}
          className="fixed top-6 right-6 z-50 p-2 rounded-full hover:bg-[#1A1A18] transition-colors"
          data-testid="skip-btn"
        >
          <X size={24} className="text-[#9E9A90]" />
        </motion.button>
      )}

      {/* Close button on entry */}
      {phase === 'entry' && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          whileHover={{ opacity: 1 }}
          onClick={handleClose}
          className="fixed top-6 right-6 z-50 p-2 rounded-full hover:bg-[#1A1A18] transition-colors"
          data-testid="close-entry-btn"
        >
          <X size={24} className="text-[#9E9A90]" />
        </motion.button>
      )}

      {/* Content */}
      <AnimatePresence mode="wait">
        {phase === 'entry' && (
          <EntryScreen key="entry" onSelect={handleContextSelect} />
        )}

        {phase === 'active' && (
          <StageDisplay
            key={`stage-${currentStageIndex}`}
            stage={STAGES[currentStageIndex]}
            context={context}
            stageIndex={currentStageIndex}
            onComplete={handleStageComplete}
          />
        )}

        {phase === 'ready' && (
          <ReadyScreen
            key="ready"
            onReplay={handleReplay}
            onDeeper={handleGoDeeper}
            onClose={handleClose}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PeakStateEngine;
